import { Component, ElementRef, Inject, ViewChild, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@app/shared/auth/auth.service';
import { Style, Size as IconSize } from '@app/shared/components/atoms/icon/icon.component';
import { Contract, ContractCsvRow, ContractInvoicesCsvRow } from '@app/shared/interfaces/contract.interface';
import { User } from '@app/shared/models/user-info.model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ContractService } from './contracts.service';
import { AlertService } from '@app/shared/components/organisms/alert/alert.service';
import { MESSAGES } from '@app/shared/utils/messages';
import { ButtonIcon, ButtonSize, ButtonStyle } from '@app/shared/components/atoms/buttons/button.type';
import { Color } from '@app/shared/models/color.model';
import { BaseTableComponent } from '@app/shared/components/organisms/table/base-table.component';
import { FilterItem } from '@app/shared/components/organisms/table/column-header/column-header.component';
import { formatDateForFilter } from '@app/shared/utils/sort';
import { mapPriceType, mapPriceTypeToColor, mapTypeBill } from '../invoice/invoices.model';
import { downloadCsv, formatEmails, toCsv } from '@app/shared/utils/utils';
import { TooltipPosition } from '@app/shared/components/atoms/icon/icon.component';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-contracts',
  templateUrl: './contracts.component.html',
  styleUrls: ['./contracts.component.scss'],
  standalone: false,
})
export class ContractsComponent implements OnInit {
  @ViewChild('inputElement') inputElement!: ElementRef;
  inputValue: string = '';

  @ViewChild(BaseTableComponent, { static: false })
  private baseTable!: BaseTableComponent<Contract>;

  protected readonly TooltipPosition = TooltipPosition;

  protected readonly formatEmails = formatEmails;

  protected readonly Style = Style;

  protected readonly ButtonStyle = ButtonStyle;

  protected readonly ButtonIcon = ButtonIcon;

  protected readonly ButtonSize = ButtonSize;

  protected readonly Color = Color;

  protected readonly IconSize = IconSize;

  hoveredTooltipId: number | null = null;

  contractsSource: Contract[] = [];

  contractsSourceSearch: Contract[] = [];

  filterItems: { [key: string]: FilterItem[] } = {};

  isLoading: boolean = true;

  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  userConnected?: User;

  rangeStart: string = '';

  rangeEnd: string = '';

  contractsRequest?: Subscription;

  isExportLoading: boolean = false;

  mapTypeBill = mapTypeBill;

  selectedFilter = '';

  currentSearchValue = '';

  filterOptions = [
    { value: 'start', label: 'Date de début' },
    { value: 'end', label: 'Date de fin' },
    { value: 'termination', label: 'Date de résiliation' },
    { value: 'startEnd', label: 'Date de début - fin' },
    { value: 'startTermination', label: 'Date de début - résiliation' },
  ];

  constructor(
    @Inject(ActivatedRoute) private route: ActivatedRoute,
    private contractsService: ContractService,
    private authService: AuthService,
    private alertService: AlertService,
  ) {
    this.authService.connectedUser$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      if (user) this.userConnected = user;
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe(() => {
      this.isLoading = true;
      this.contractsSource = [];
      this.contractsSourceSearch = [];
      this.contractsRequest = this.getContractsRequest({});
    });
  }

  private getContractsRequest(filter: { [key: string]: string }) {
    this.isLoading = true;
    return this.contractsService.getContracts(filter).subscribe({
      next: (response) => {
        this.contractsSource = response;
        this.contractsSourceSearch = response;
        const contractsFiltredHeaderList = ['typeBill', 'formulaLabel'];
        this.generateFilterItems(contractsFiltredHeaderList as (keyof Contract)[]);
        this.getContractsSearch(this.currentSearchValue);
        this.isLoading = false;
      },
      error: (error) => {
        this.alertService.error(MESSAGES.DEFAULT_ERROR);
        console.error(`Erreur lors de la récupération des contras`, error);
        this.isLoading = false;
      },
    });
  }

  /**
   * Récupère la valeur d'un objet à partir d'un chemin
   * @param obj L'objet source dans lequel chercher
   * @param path Le chemin sous forme de chaîne (ex: "site.name")
   * @returns La valeur trouvée ou undefined si le chemin n'existe pas
   */
  getValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  /**
   * Construit une chaîne de recherche à partir des propriétés d'un contrat.
   * @param contract Le contrat dont on veut construire la chaîne de recherche
   * @returns Une chaîne combinant toutes les valeurs pertinentes du contrat en minuscules
   */
  buildSearchString(contract: Contract): string {
    const keys = ['prm', 'name', 'number', 'site.name', 'producer.name'];
    return keys
      .map((key) => this.getValue(contract, key))
      .filter((val) => val != null)
      .join(' ')
      .toLowerCase();
  }

  /**
   * Méthode permettant de chercher en fonction de différents critères
   * @param searchValue La valeur à rechercher
   */
  getContractsSearch(searchValue: string) {
    this.isLoading = true;
    this.currentSearchValue = searchValue;

    if (searchValue === '') {
      this.contractsSourceSearch = this.contractsSource;
    } else {
      const lowerSearchValue = searchValue.toLowerCase();

      this.contractsSourceSearch = this.contractsSource.filter((item) => {
        const searchIndex = this.buildSearchString(item);
        return searchIndex.includes(lowerSearchValue);
      });
    }

    this.isLoading = false;

    this.baseTable?.resetPage();
  }

  generateFilterItems = (headerList: (keyof Contract)[]) => {
    const setsMap = new Map<keyof Contract, Set<string>>();
    this.filterItems = {};

    headerList.forEach((field) => {
      setsMap.set(field, new Set<string>());
      this.filterItems[field] = [];
    });

    this.contractsSource.forEach((item) => {
      headerList.forEach((field) => {
        const fieldValue = item[field] as string;

        if (!setsMap.get(field)?.has(fieldValue)) {
          setsMap.get(field)?.add(fieldValue);
          this.filterItems[field].push({ name: fieldValue, checked: false });
        }
      });
    });
  };

  /**
   * Méthode pour le rafraichissement des contrats dans le tableau
   */
  refreshContracts() {
    if (this.contractsRequest) {
      this.contractsRequest.unsubscribe();
    }

    this.contractsRequest = this.getContractsRequest({});
    if (this.inputElement) {
      this.inputValue = '';
    }

    this.rangeStart = '';
    this.rangeEnd = '';
    this.selectedFilter = '';
    this.currentSearchValue = '';

    this.baseTable?.resetPage();
  }

  /**
   * Méthode pour exporter les contrats dans le tableau
   */
  exportContracts() {
    const headers: { key: keyof ContractCsvRow; label: string }[] = [
      { key: 'Nom', label: 'Nom' },
      { key: 'Numero', label: 'Numéro' },
      { key: 'Titulaire', label: 'Titulaire' },
      { key: 'Site', label: 'Site' },
      { key: 'PRM', label: 'PRM' },
      { key: 'Formule', label: 'Formule' },
      { key: 'Type', label: 'Type' },
      { key: 'DateDebut', label: 'Date début' },
      { key: 'DateFin', label: 'Date de fin' },
      { key: 'DateResiliation', label: 'Date résiliation' },
      { key: 'DateModification', label: 'Date modification' },
      { key: 'Contact', label: 'Contact' },
    ];

    const rows: ContractCsvRow[] = this.contractsSourceSearch.map((contract) => ({
      Nom: contract.name,
      Numero: contract.number,
      Titulaire: contract.producer.name,
      Site: contract.site.name,
      PRM: contract.prm,
      Formule: contract.formulaLabel,
      Type: mapTypeBill(contract.typeBill),
      DateDebut: DateTime.fromFormat(contract.startDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
      DateFin: DateTime.fromFormat(contract.endDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy'),
      DateResiliation: contract.terminationDate
        ? DateTime.fromFormat(contract.terminationDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
        : '-',
      DateModification: contract.updatedAt
        ? DateTime.fromMillis(Number(contract.updatedAt)).toFormat('dd/MM/yyyy HH:mm')
        : '-',
      Contact: contract.contacts.map((contact) => contact.email).join(' , '),
    }));

    downloadCsv(`billing_contrats_${DateTime.now().toFormat('yyyyLLdd')}.csv`, toCsv<ContractCsvRow>(rows, headers));
  }

  /**
   * Méthode pour exporter les contrats avec les factures dans le tableau
   */
  exportContractsFactures(): void {
    const headers: { key: keyof ContractInvoicesCsvRow; label: string }[] = [
      { key: 'Nom', label: 'Nom' },
      { key: 'Numero', label: 'Numero' },
      { key: 'PRM', label: 'PRM' },
      { key: 'Facture', label: 'Facture' },
    ];

    this.isExportLoading = true;

    const params: {
      startDate?: string;
      endDate?: string;
      selectedFilter?: string;
      fieldSearch?: string;
    } = {};

    if (this.rangeStart) params.startDate = formatDateForFilter(this.rangeStart, 'INF');
    if (this.rangeEnd) params.endDate = formatDateForFilter(this.rangeEnd, 'SUP');
    if (this.selectedFilter) params.selectedFilter = this.selectedFilter;
    if (this.currentSearchValue) params.fieldSearch = this.currentSearchValue;

    this.contractsService.getContractsInvoices(params).subscribe({
      next: (rows) => {
        downloadCsv(
          `billing_contrats_factures_${DateTime.now().toFormat('yyyyLLdd')}.csv`,
          toCsv<ContractInvoicesCsvRow>(rows, headers),
        );
        this.isExportLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.alertService.error('Erreur lors de l’export des contrats et factures');
        this.isExportLoading = false;
      },
    });
  }

  async onStartDateChanged(newStartDate: string) {
    this.rangeStart = newStartDate;
    const startDate = formatDateForFilter(newStartDate, 'INF');
    const endDate = formatDateForFilter(this.rangeEnd!, 'SUP');

    this.getContractsRequest({
      selectedFilter: this.selectedFilter,
      startDate,
      endDate,
    });

    this.baseTable?.resetPage();
  }

  async onEndDateChanged(newEndDate: string) {
    this.rangeEnd = newEndDate;
    const endDate = formatDateForFilter(newEndDate, 'SUP');
    const startDate = formatDateForFilter(this.rangeStart!, 'INF');

    this.getContractsRequest({
      selectedFilter: this.selectedFilter,
      startDate,
      endDate,
    });

    this.baseTable?.resetPage();
  }

  async onFilterChanged(newFilter: string) {
    this.selectedFilter = newFilter;

    let startDate = '';
    let endDate = '';

    if (!newFilter) {
      this.rangeStart = '';
      this.rangeEnd = '';
    } else if (this.rangeStart && this.rangeEnd) {
      startDate = formatDateForFilter(this.rangeStart, 'INF');
      endDate = formatDateForFilter(this.rangeEnd, 'SUP');
    }

    this.getContractsRequest({
      selectedFilter: newFilter,
      startDate,
      endDate,
    });
    this.baseTable?.resetPage();
  }

  showTooltip(id: number) {
    this.hoveredTooltipId = id;
  }

  hideTooltip() {
    this.hoveredTooltipId = null;
  }

  protected readonly mapPriceType = mapPriceType;
  protected readonly mapPriceTypeToColor = mapPriceTypeToColor;
}
